// @import_dependencies
import { store } from 'app_storage/redux-storage'
// @end

// @import services
import ReactSelectService from 'app_services/components/reactSelectService'
// @end

// @import utilities
import { Http, HttpCustomStructure } from 'app_utils/http'
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'
// @end

// @import types
import { IHttpResponse } from 'app_types/httpTypes'
import { ICourseSchedulingsHttpResponse, ICourseSchedulingHttpResponse, ICourseSchedulingReport, ICourseSchedulingListItem, ForceStatus } from 'app_types/admin/courseScheduling/courseSchedulingTypes'
import { blobToFile } from 'app_utils/functions/blobToFile'
// @end

interface ResponseListCourseSchedulingFilter{
  itemList: ICourseSchedulingListItem[],
  pagination: {
    total: number,
    perPage: number,
    page: number,
  },
}

class CourseSchedulingService {

  private token: string
  private storeObject: any
  private Http: any
  private screenNotification: ScreenNotificationsUtil
  private reactSelectService: ReactSelectService

  /*===============================================
  =            Estructura de un metodo            =
  ================================================
    // La estructura de un metodo debe ser la siguiente:
    public methodName = () => {}
  /*======  End of Estructura de un metodo  =====*/

  constructor () {
    this.Http = Http
    this.storeObject = store
    this.screenNotification = new ScreenNotificationsUtil()
    this.reactSelectService = new ReactSelectService()
  }

  refreshAuthToken = () => {
    let auth = this.storeObject.getState().auth
    this.token = (auth.user?.token) ? auth.user.token : ''
  }

  async list(params: any = {}) {
    this.refreshAuthToken()

    let responseData: ICourseSchedulingsHttpResponse

    const queryData: HttpCustomStructure = {
      method: 'GET',
      url: `/api/admin/course-scheduling`,
      auth_token: this.token,
      params: {
        ...params,
      },
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) {
      return responseData
    } else {
      this.screenNotification.fire(responseData)
    }

    return null
  }


  insertOrUpdate = async (postData: any, action: string = 'create') => {
    this.refreshAuthToken()

    let responseData: ICourseSchedulingHttpResponse

    const queryData: HttpCustomStructure = {
      method: 'POST',
      url: `/api/admin/course-scheduling/${action}`,
      headers: new Headers({
        Accept: 'application/json, application/xml, text/plain, text/html, *.*',
        'Content-Type': 'multipart/form-data',
      }),
      auth_token: this.token,
      params: postData,
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) {
      return responseData.scheduling
    } else {
      this.screenNotification.fire(responseData)
    }

    return null
  }

  getOne = async (id: string) => {
    this.refreshAuthToken()

    let responseData: ICourseSchedulingHttpResponse

    const queryData: HttpCustomStructure = {
      method: 'GET',
      url: `/api/admin/course-scheduling/${id}`,
      auth_token: this.token,
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) {
      return responseData.scheduling
    } else {
      this.screenNotification.fire(responseData)
    }

    return null
  }

  remove = async (id: string) => {
    this.refreshAuthToken()

    let responseData: IHttpResponse

    const queryData: HttpCustomStructure = {
      method: 'DELETE',
      url: `/api/admin/course-scheduling/delete/${id}`,
      auth_token: this.token,
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) {
      return true
    } else {
      this.screenNotification.fire(responseData)
    }

    return null
  }

  generatePdfReport = async (params: any) => {
    this.refreshAuthToken()

    let responseData: ICourseSchedulingReport

    const queryData: HttpCustomStructure = {
      method: 'GET',
      url: `/api/admin/course-scheduling/generate-report`,
      auth_token: this.token,
      params: {
        ...params,
      },
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) {
      return responseData
    } else {
      this.screenNotification.fire(responseData)
    }

    return null
  }

  generateSurveyReport = async (params: any) => {
    this.refreshAuthToken()

    let responseData: ICourseSchedulingReport

    const queryData: HttpCustomStructure = {
      method: 'POST',
      url: `/api/data/survey/generate-report`,
      auth_token: this.token,
      params: {
        ...params,
      },
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) {
      return responseData
    } else {
      this.screenNotification.fire(responseData)
    }

    return null
  }

  /**
   * @INFO Obtener el listado de course scheduling utilizando el filtro
   */
  public getListWithFilter = async (params: any): Promise<ResponseListCourseSchedulingFilter | undefined> => {

    const httpParams: any = {
      pageNumber: params?.page ? params?.page : 0,
      nPerPage: params?.perPage ? params?.perPage : 10,
      isActive: false
    }

    if (params?.search) {
      httpParams.search = params?.search
    }

    if (params?.course_scheduling_code) {
      httpParams.course_scheduling_code = params?.course_scheduling_code
    }

    if (params?.service_id) {
      httpParams.service_id = params?.service_id
    }

    if (params?.mode) {
      httpParams.schedulingMode = this.reactSelectService.formatReactSelectResponse(params.mode)
    }

    if (params?.schedulingStatus) {
      httpParams.schedulingStatus = this.reactSelectService.formatReactSelectResponse(params.schedulingStatus)
    }

    if (params?.schedulingType) {
      httpParams.schedulingType = this.reactSelectService.formatReactSelectResponse(params.schedulingType)
    }

    if (params?.regional) {
      httpParams.regional = this.reactSelectService.formatReactSelectResponse(params.regional)
    }

    if (params?.modular) {
      httpParams.modular = this.reactSelectService.formatReactSelectResponse(params.modular)
    }

    if (params?.start_date) {
      httpParams.start_date = params?.start_date
    }

    if (params?.end_date) {
      httpParams.end_date = params?.end_date
    }

    if (params?.account_executive) {
      httpParams.account_executive = params?.account_executive
    }

    if (params?.program_course_name) {
      httpParams.program_course_name = params?.program_course_name
    }

    if (params?.client) {
      httpParams.client = params?.client
    }

    if (params?.company) {
      httpParams.company = params?.company?.value
    }

    let itemList: ICourseSchedulingsHttpResponse | null = await this.list(httpParams)

    if (itemList?.status_code === 'success') {
      return {
        itemList: itemList?.schedulings,
        pagination: {
          total: itemList?.total_register,
          perPage: itemList?.nPerPage,
          page: itemList?.pageNumber,
        }
      }
    } else {
      return undefined;
    }
  }

  schedulingConfirmedByMonth = async (params?: {months?: number}) => {
    this.refreshAuthToken()

    let responseData: ICourseSchedulingReport

    const queryData: HttpCustomStructure = {
      method: 'POST',
      url: `/api/data/course-scheduling/fetch-course-confirmed-by-month`,
      auth_token: this.token,
      params: {
        ...params,
      },
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) {
      return responseData
    } else {
      this.screenNotification.fire(responseData)
    }

    return null
  }

  forceStatusService = async (params?: {id?: number, status: ForceStatus}) => {
    this.refreshAuthToken()

    let responseData: any

    const queryData: HttpCustomStructure = {
      method: 'POST',
      url: `/api/admin/course-scheduling/${params?.id}/force-status-service`,
      auth_token: this.token,
      params: {
        ...params,
      },
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) {
      return responseData
    } else {
      this.screenNotification.fire(responseData)
    }

    return null
  }

  reactivateService = async (params?: {id?: number}) => {
    this.refreshAuthToken()

    let responseData: any

    const queryData: HttpCustomStructure = {
      method: 'POST',
      url: `/api/admin/course-scheduling/${params?.id}/reactivate-service`,
      auth_token: this.token,
      params: {
        ...params,
      },
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) {
      return responseData
    } else {
      this.screenNotification.fire(responseData)
    }

    return null
  }

  duplicateService = async (params?: {id?: number}) => {
    this.refreshAuthToken()

    let responseData: any

    const queryData: HttpCustomStructure = {
      method: 'POST',
      url: `/api/admin/course-scheduling/${params?.id}/duplicate-service`,
      auth_token: this.token,
      params: {
        ...params,
      },
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) {
      return responseData
    } else {
      this.screenNotification.fire(responseData)
    }

    return null
  }

  sincroniceServiceMoodle = async (courseSchedulingId: string) => {
    this.refreshAuthToken()

    let responseData: any

    const queryData: HttpCustomStructure = {
      method: 'POST',
      url: `/api/admin/course-scheduling/sincronice-service-moodle`,
      auth_token: this.token,
      params: {
        courseSchedulingId,
      },
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) {
      return responseData
    } else {
      this.screenNotification.fire(responseData)
    }

    return null
  }

  downloadCalendar = async (params: any) => {
    this.refreshAuthToken()

    const queryData: HttpCustomStructure = {
      method: 'GET',
      url: `/api/admin/course-scheduling/download-calendar`,
      auth_token: this.token,
      params: {
        ...params,
      },
      responseType: 'blob'
    }

    const blob = await this.Http.send(queryData)
    if (blob.size > 0) {
      blobToFile(blob, 'CalendarioIcontec.ics')
      return null
    } else {
      this.screenNotification.fire({
        status: 'error',
        message: 'No hay proximas sesiones programadas'
      })
    }
  }
}

export default CourseSchedulingService
